import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'

import { AuthenticatedCustomerInfoContext, CustomerInfo } from 'contexts/CustomerInfoContext'
import apiClient from 'helpers/clients/apiClient'
import { handleGTMDataLayer } from 'utils/handleGTMDataLayer'

export const useProvideCustomerInfo = (): AuthenticatedCustomerInfoContext => {
  const router = useRouter()

  const [isLoading, setIsLoading] = useState(true)
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    customer: undefined,
    organization: undefined,
  })

  const getCustomerInfo = async () => {
    setIsLoading(true)

    const response = await apiClient.getCustomerProfile()
    if (!response.success) {
      console.log(`Error on getting customer`, response.error)
      setIsLoading(false)
      router.push('/login?action=logout')
      return
    }

    const customer = response.data
    if (!customer) {
      setCustomerInfo(() => ({
        customer: undefined,
        organization: undefined,
      }))
      setIsLoading(false)
      return
    }

    const organizationResponse = await apiClient.getSelectedOrganization()
    if (!organizationResponse.success) {
      setIsLoading(false)
      router.push('/login?action=logout')
      return
    }
    const organization = organizationResponse.data

    setCustomerInfo(() => ({
      customer: customer ?? undefined,
      organization: organization ?? undefined,
    }))

    handleGTMDataLayer(customer ?? undefined)

    setIsLoading(false)
  }

  useEffect(() => {
    getCustomerInfo()
  }, [])

  const signIn = async () => {
    getCustomerInfo()
  }

  const signOut = async () => {
    setCustomerInfo({
      customer: undefined,
      organization: undefined,
    })
  }

  const selectOrganizationOrPersonalAccount = async (organizationId: number | undefined): Promise<boolean> => {
    const setOrganization = await apiClient.selectOrganizationOrPersonalAccount(organizationId)
    if (!setOrganization.success || !setOrganization.data) {
      return false
    }

    await signIn()
    return true
  }

  return { isLoading, customerInfo, signIn, signOut, selectOrganizationOrPersonalAccount }
}
